'use client';

import { useEffect } from 'react';
import { sendDataLayerEvent } from '@utils/send-data-layer-event/send-data-layer-event';

/**
 * @description Fires a GA4 sign_up event via dataLayer once if a short-lived cookie flag exists, then deletes the cookie.
 * Cookie may be encoded 0–2 times. Expected decoded shape: "1;method;type" (e.g., "1;email;business").
 */
export const useGaSignUpOnce = (cookieName = 'ga_signup') => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const raw = document.cookie.split('; ').find((row) => row.startsWith(`${cookieName}=`));
    if (!raw) return;

    try {
      const encoded = raw.split('=')[1] || '';

      // Decode up to 3 times until literal semicolons appear, to tolerate double-encoding like "1%253Bemail%253Bbusiness"
      let decoded = encoded;
      for (let i = 0; i < 3; i += 1) {
        if (decoded.includes(';')) break; // already has literal semicolons
        if (/%[0-9A-Fa-f]{2}/.test(decoded)) {
          try {
            decoded = decodeURIComponent(decoded);
          } catch {
            break; // stop if decoding fails
          }
        } else {
          break; // nothing more to decode
        }
      }

      const parts = decoded.split(';'); // ["1", "email", "business"]
      const method = (parts[1] || 'email').trim();
      const accountType = (parts[2] || 'personal').trim().toLowerCase();

      // Optional debug: remove after verification
      // eslint-disable-next-line no-console
      console.log('[GA Bridge] cookie parsed:', {
        encoded, decoded, parts, method, accountType,
      });

      sendDataLayerEvent({
        event: 'sign_up',
        method,
        account_type: accountType,
      });
    } finally {
      // Delete cookie to avoid duplicate firing on refresh/back
      document.cookie = `${cookieName}=; Max-Age=0; path=/`;
    }
  }, [cookieName]);
};
